import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Plus, Pencil, Trash2, Wallet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const TIPOS = [
  { v: "pix", l: "PIX" },
  { v: "boleto", l: "Boleto" },
  { v: "parcelado", l: "Parcelado" },
  { v: "locacao_mensal", l: "Locação Mensal" },
  { v: "outros", l: "Outros" },
];

const TIPO_COR = {
  pix: "bg-emerald-100 text-emerald-700",
  boleto: "bg-blue-100 text-blue-700",
  parcelado: "bg-amber-100 text-amber-700",
  locacao_mensal: "bg-purple-100 text-purple-700",
  outros: "bg-slate-100 text-slate-700",
};

const EMPTY = {
  nome: "",
  descricao: "",
  parcelas: 1,
  entrada_percentual: 0,
  juros_percentual: 0,
  prazo_dias: 0,
  tipo: "outros",
  ativo: true,
};

export default function CondicoesPagamento() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const canEdit = ["diretor", "supervisao", "gerente", "financeiro"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/financeiro/condicoes-pagamento");
      setItems(data || []);
    } catch (e) {
      toast.error("Falha ao carregar condições");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const abrirNovo = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const abrirEdicao = (c) => {
    setEditing(c);
    setForm({ ...c });
    setOpen(true);
  };

  const salvar = async () => {
    if (!form.nome.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao || null,
        parcelas: Number(form.parcelas) || 1,
        entrada_percentual: Number(form.entrada_percentual) || 0,
        juros_percentual: Number(form.juros_percentual) || 0,
        prazo_dias: Number(form.prazo_dias) || 0,
        tipo: form.tipo,
        ativo: !!form.ativo,
      };
      if (editing) {
        await api.patch(`/financeiro/condicoes-pagamento/${editing.id}`, payload);
        toast.success("Condição atualizada");
      } else {
        await api.post("/financeiro/condicoes-pagamento", payload);
        toast.success("Condição criada");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remover = async (c) => {
    if (!window.confirm(`Remover "${c.nome}"?`)) return;
    try {
      await api.delete(`/financeiro/condicoes-pagamento/${c.id}`);
      toast.success("Removida");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  return (
    <div className="space-y-5" data-testid="condicoes-pagamento-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Wallet size={28} style={{ color: "#1B3A6B" }} />
          <div>
            <h1 className="text-3xl font-display font-bold" style={{ color: "#1B3A6B" }}>
              Condições de Pagamento
            </h1>
            <p className="text-sm text-muted-foreground">
              Cadastradas no Financeiro e reutilizáveis em todas as propostas.
            </p>
          </div>
        </div>
        {canEdit && (
          <Button onClick={abrirNovo} className="text-white" style={{ background: "#F97316" }} data-testid="nova-condicao-btn">
            <Plus size={14} className="mr-2" /> Nova condição
          </Button>
        )}
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-center">Parcelas</TableHead>
              <TableHead className="text-center">Entrada</TableHead>
              <TableHead className="text-center">Prazo</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-center">Ativo</TableHead>
              {canEdit && <TableHead className="text-right">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="inline animate-spin mr-2" />Carregando…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Nenhuma condição cadastrada.</TableCell></TableRow>
            ) : items.map((c) => (
              <TableRow key={c.id} data-testid={`condicao-${c.id}`}>
                <TableCell className="font-medium">{c.nome}</TableCell>
                <TableCell><Badge className={TIPO_COR[c.tipo] || TIPO_COR.outros} variant="secondary">{TIPOS.find((t) => t.v === c.tipo)?.l || c.tipo}</Badge></TableCell>
                <TableCell className="text-center font-mono">{c.parcelas}</TableCell>
                <TableCell className="text-center font-mono">{c.entrada_percentual ? `${c.entrada_percentual}%` : "—"}</TableCell>
                <TableCell className="text-center font-mono">{c.prazo_dias ? `${c.prazo_dias}d` : "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate" title={c.descricao}>{c.descricao || "—"}</TableCell>
                <TableCell className="text-center">{c.ativo ? <span className="text-emerald-600">✓</span> : <span className="text-zinc-400">—</span>}</TableCell>
                {canEdit && (
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => abrirEdicao(c)} data-testid={`editar-${c.id}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remover(c)} data-testid={`remover-${c.id}`}><Trash2 size={14} className="text-red-600" /></Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Editar condição" : "Nova condição de pagamento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input data-testid="cond-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Sinal 40% + 30/60" />
            </div>
            <div>
              <Label>Descrição (aparece no PDF)</Label>
              <Textarea data-testid="cond-descricao" rows={2} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <select data-testid="cond-tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="w-full border rounded h-9 px-2 text-sm bg-background">
                  {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div>
                <Label>Parcelas</Label>
                <Input data-testid="cond-parcelas" type="number" min={1} value={form.parcelas} onChange={(e) => setForm({ ...form, parcelas: e.target.value })} />
              </div>
              <div>
                <Label>Entrada (%)</Label>
                <Input data-testid="cond-entrada" type="number" min={0} max={100} step="0.1" value={form.entrada_percentual} onChange={(e) => setForm({ ...form, entrada_percentual: e.target.value })} />
              </div>
              <div>
                <Label>Juros (%)</Label>
                <Input data-testid="cond-juros" type="number" min={0} step="0.01" value={form.juros_percentual} onChange={(e) => setForm({ ...form, juros_percentual: e.target.value })} />
              </div>
              <div>
                <Label>Prazo (dias)</Label>
                <Input data-testid="cond-prazo" type="number" min={0} value={form.prazo_dias} onChange={(e) => setForm({ ...form, prazo_dias: e.target.value })} />
              </div>
              <div className="flex items-end gap-2">
                <Switch data-testid="cond-ativo" checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} />
                <Label>Ativo</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
              <Button onClick={salvar} disabled={saving} className="text-white" style={{ background: "#1B3A6B" }} data-testid="cond-salvar">
                {saving ? <Loader2 className="mr-2 animate-spin" size={14} /> : null}
                {editing ? "Salvar alterações" : "Criar condição"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
